import DateUtilsService from 'external-services/date-utils-service/index';
import { ICallbackInfo, PeriodicDefaultTime } from './types';
import { IBooking, IServiceItem } from 'shared-types/SharedTypes';
import GeneralSettingsService from 'external-services/general-settings-service/generalSettings.service';
import store from 'floorplanStore';
import { floorplanBookingsActions } from 'floorplan-v2/app/reducers/bookings/slice';
import { DEFAULT_CONFLICT_MINS } from 'floorplan-v2/app/appRoot/constants';
import { errorStateActions } from 'app/reducers/errorState/slice';

const NS = `PeriodicUpdateService`;

export const FLOORPLAN_HEADER_RENDER = 'floorplan-header-render';

class PeriodicUpdateService {
  private timezoneId: string;

  private isStopTrack: boolean = false;

  private mainTrack: NodeJS.Timeout | null = null;
  private mainTrackFuncs: ICallbackInfo[] = [];
  private defaultTime: number = PeriodicDefaultTime;
  private defaultUpcomingBookingTime: number = DEFAULT_CONFLICT_MINS;

  // private preliminaryTimeTracks: NodeJS.Timeout[] = [];
  // private isHoldState = false;

  /**
   * This property is only for unit test
   */
  private calledSecond: number;

  public init(timezoneId?: string, defaultTime?: number) {
    // this.endTimer();
    // this.removeAllMainTrackFuncs();
    if (timezoneId) this.timezoneId = timezoneId;
    if (defaultTime) this.defaultTime = defaultTime;
    const settings = GeneralSettingsService.getSettingsCache();
    this.isStopTrack = false;
    this.calledSecond = -1;
    this.addMainTrackFuncs({
      name: 'checkTableForUpcomingBookings',
      callback: this.checkTableForUpcomingBookings.bind(this),
    })
  }

  public startTimer(isForced?: boolean) {
    if (!this.timezoneId) {
      store.dispatch(
        errorStateActions.setErrorState({
          errorTitle: 'Timezone error',
          errorMessage: 'Your timezone has been incorrectly set, please try reloading the app.',
          accordionDetail: '',
        })
      );
      return;
    }
    if (isForced && !!this.mainTrack) {
      this.endTimer();
    } else if (!isForced && !!this.mainTrack) {
      return;
    }

    const disabled = localStorage.getItem('disable-periodic-update');
    if (disabled) {
      return;
    }

    const currentTime = DateUtilsService.getVenueTime(this.timezoneId);

    const _seconds = 60 - currentTime.getSeconds();

    const delaySeconds = _seconds === 60 ? 0 : _seconds;
    this.isStopTrack = false;
    setTimeout(() => {
      this.runMainTrackFuncs(this.mainTrackFuncs);
      this.mainTrack = setInterval(() => {
        this.runMainTrackFuncs(this.mainTrackFuncs);
      }, this.defaultTime);
    }, delaySeconds * 1000);
  }

  public forceStart() {
    const tracks = [...this.mainTrackFuncs];
    this.runMainTrackFuncs(tracks);
  }

  public endTimer() {
    this.mainTrack && clearInterval(this.mainTrack);
    this.mainTrack = null;
  }

  private runMainTrackFuncs(tracks: ICallbackInfo[]) {
    if (this.isStopTrack) return;
    if (this.calledSecond === -1)
      this.calledSecond = DateUtilsService.getVenueTime(this.timezoneId).getSeconds();
    tracks.forEach((track: ICallbackInfo) => {
      track.callback();
    });
  }

  public addMainTrackFuncs(info: ICallbackInfo) {
    this.removeMainTrackFuncs(info.name);
    this.mainTrackFuncs.push(info);
  }

  public removeMainTrackFuncs(trackName: string) {
    const index = this.mainTrackFuncs.findIndex((track) => track.name === trackName);
    if (index !== -1) {
      this.mainTrackFuncs.splice(index, 1);
    }
  }

  public removeAllMainTrackFuncs() {
    this.mainTrackFuncs = [];
  }

  public setIsStopTrack(isStop: boolean) {
    this.isStopTrack = isStop;
  }

  // public addTrackInstance(instanceInfo: ITimeTrackInfo) : NodeJS.Timeout | null {
  //   if(this.isHoldState) return null;
  //   const {callback, trackTime = 1000} = instanceInfo;
  //   const interval = setTimeout(() => {
  //     !!callback && callback();
  //   }, trackTime);
  //   this.preliminaryTimeTracks.push(interval);
  //   return interval;
  // }

  // public removeTrackInstance(intervalId: NodeJS.Timeout): void {
  //   const index = this.preliminaryTimeTracks.indexOf(intervalId);
  //   if (index !== -1) {
  //     this.preliminaryTimeTracks.splice(index, 1);
  //   }
  //   clearInterval(intervalId);
  //   console.log(NS, 'removed time track', intervalId);
  // }

  // public removeAllTrackInstance(): void {
  //   this.isHoldState = true;
  //   const ids = [...this.preliminaryTimeTracks];
  //   for(let i = 0; i < ids.length; i++) {
  //     this.removeTrackInstance(ids[i]);
  //   }
  //   this.isHoldState = false;
  // }

  /*
    * This function is for checking the existing table for upcoming bookings
   */

  // this gets triggered periodically - hence need to filter the bookings here as well.
  public checkTableForUpcomingBookings(): void {
    const bookings = store.getState().floorplanBookingsReducer.floorplanBookings;
    const selectedTime = store.getState().floorplanFilterReducer.selectedFloorplanTime; // need to filter bookings based on times selected in FP header

    const filteredBooking = this.getUpcomingBookings(bookings, selectedTime, this.defaultUpcomingBookingTime);
    store.dispatch(floorplanBookingsActions.setUpcomingBookings(filteredBooking));
  }

  // this gets called from the reducer
  public getUpcomingBookings(bookings: IBooking[], selectedTime: Date, mins = DEFAULT_CONFLICT_MINS): IBooking[] {
    if(!selectedTime || !bookings) {
      return [];
    }

    const selectedTimeMS = selectedTime.getTime();

    // removed filterBookingsByDate as there is no need to filter by 8 hours (upcomingBookings will filter next 90 mins)
    const upcomingThresholdMS = mins * 60 * 1000;

    const upcomingBookings = bookings.filter((booking) => {
      // should not filter out bookings by selected service. If bookings exist on a table, they should always show
      const bookingTimeMS = booking.time.getTime();
      const upcomingBookingEndMS = selectedTimeMS + upcomingThresholdMS;

      // if the booking is within 90 mins, but more than selected time, then it is an upcoming booking
      return booking.time > selectedTime && bookingTimeMS <= upcomingBookingEndMS;
    });
    return upcomingBookings;
  }



  /**
   * This function is only for unit test
   */

  public _getTimezoneIdAndDefaultTimeForUnitTest() {
    return {
      timezoneId: this.timezoneId,
      defaultTime: this.defaultTime,
    };
  }

  /**
   * This function is only for unit test
   */

  public _getMainTrackFuncsForUnitTest() {
    return this.mainTrackFuncs;
  }

  /**
   * This function is only for unit test
   */

  public _getCalledSecondForUnitTest() {
    return this.calledSecond;
  }
}

const instance = new PeriodicUpdateService();
export default instance as PeriodicUpdateService;
